'use client';

import {ConfirmationModal} from '@rocket-ui/components';
import React, {FC, PropsWithChildren, Reducer, useCallback, useReducer, useRef} from 'react';
import {ConfirmWithUserRequest, UserInputRequest, UserInteractionContext} from './context';
import {showNotification} from './toasts';

interface UserInteractionState {
	props: ConfirmWithUserRequest;
	resolve: (result: boolean | string) => void;
}

interface ShowUserInteractionAction {
	type: 'show';
	payload: UserInteractionState;
}

interface HideUserInteractionAction {
	type: 'hide';
}

type UserInteractionAction = ShowUserInteractionAction | HideUserInteractionAction;

export const WithUserInteraction: FC<PropsWithChildren<any>> = ({children}) => {
	const inputRef = useRef<HTMLTextAreaElement | null>(null);
	const [state, dispatch] = useReducer<Reducer<UserInteractionState | null, UserInteractionAction>>((state, action) => {
		if (action.type === 'show') return action.payload;
		if (action.type === 'hide') return null;

		return state;
	}, null);
	const isOpen = state !== null;

	const close = () => dispatch({type: 'hide'});

	const confirmWithUser = async (props: ConfirmWithUserRequest) => {
		return new Promise<boolean | string>((resolve) => {
			dispatch({type: 'show', payload: {props, resolve}});
		});
	};

	const handleConfirm = (value: string | void) => {
		close();
		const result = !value ? true : value;
		state?.resolve(result);
	};

	const handleCancel = () => {
		close();
		state?.resolve(false);
	};

	const handleTextareaLoaded = useCallback((el: HTMLTextAreaElement) => {
		if (!el) return;

		inputRef.current = el;

		el.style.height = 'auto';
		el.style.height = el.scrollHeight + 2 + 'px';

		setTimeout(() => {
			el.selectionStart = el.value.length;
			el.selectionEnd = el.value.length;
		}, 0);

		el.focus();
	}, []);

	const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const el = e.target;
		el.style.height = 'auto';
		el.style.height = el.scrollHeight + 2 + 'px';
	}, []);

	const requestUserInput = async (props: UserInputRequest) => {
		return new Promise<string | boolean>((resolve) => {
			dispatch({
				type: 'show',
				payload: {
					props: {
						title: props.title,
						message: (
							<div className={'font-semibold'}>
								<textarea
									defaultValue={props.value}
									className={
										'block w-full mt-1.5 p-0 text-2xl font-semibold tracking-tight border-none outline-none resize-none font-inherit'
									}
									onChange={handleTextareaChange}
									rows={1}
									placeholder={props.placeholder}
									ref={handleTextareaLoaded}
								/>
							</div>
						),
						confirmButtonLabel: props.confirmButtonLabel || 'ОК',
						rejectButtonLabel: props.rejectButtonLabel || 'Отмена',
						size: 'sm',
					},
					resolve: (result: boolean | string) => {
						resolve(result ? inputRef.current?.value ?? '' : false);
					},
				},
			});
		});
	};

	const notifyUser = (message: string, props?: {fixed?: boolean; delay?: number}) => {
		const options: Record<string, any> = {
			autoClose: props?.delay ?? 2000,
		};

		if (props?.fixed) delete options.autoClose;

		showNotification(message, options);
	};

	return (
		<>
			<UserInteractionContext.Provider value={{isOpen, confirmWithUser, requestUserInput, notifyUser}}>
				{children}
			</UserInteractionContext.Provider>

			<ConfirmationModal
				isOpen={isOpen}
				title={state?.props?.title || ''}
				options={state?.props.options}
				confirmText={state?.props?.confirmButtonLabel || ''}
				cancelText={state?.props?.rejectButtonLabel || ''}
				description={state?.props?.message || ''}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				size={state?.props.size}
			/>
		</>
	);
};
